import { useState } from "react";
import { z } from "zod";
import {
  Box,
  WidgetEmpty,
  WidgetSkeleton,
  ChartFrame,
  ChartText,
  ChartTooltipCard,
  Pie,
  LegendOrdinal,
  useWidgetInstance,
  type PieArcDatum,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { WIDGET_DIMENSIONS, WIDGET_DIMENSION_OPTIONS } from "#app/widgets/dashboards/use-dimension-counts";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import {
  useShapedSlices,
  sliceDrilldownIds,
  SLICE_SHOW_OPTIONS,
  SLICE_SORT_OPTIONS,
  type DimensionSlice,
} from "#app/widgets/dashboards/use-dimension-slices";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { WidgetChartTooltip } from "../shared/widget-chart-tooltip";
import { workloadTitle } from "../shared/derived-titles";

/**
 * `type-mix` — a themed pie/donut of projects grouped by any dimension, on the
 * custom `ChartFrame` path. Pressure-tests the primitive path (`Pie` with a
 * `centroid` render for slice labels, a center total, `LegendOrdinal`) AND the
 * Display / Data / Filters settings shape: v3-style icon rows (toggles +
 * compact selects) over the shared dimension vocabulary, plus the
 * `issueFilters` blob every part of the data layer folds. Top-N slicing folds
 * the tail into an "Other" wedge.
 */
export const typeMixConfigSchema = z
  .looseObject({
    donut: z.boolean().default(true),
    percentages: z.boolean().default(true),
    legend: z.boolean().default(true),
    measure: z.enum(["count"]).default("count"),
    groupBy: z.enum(WIDGET_DIMENSIONS).default("type"),
    show: z.enum(["top5", "top8", "top12", "all"]).default("top8"),
    sort: z.enum(["volume", "alpha"]).default("volume"),
    filters: widgetFiltersSchema.nullish(),
    // Geometry stays configurable data (render reads it) without settings rows —
    // the editor keeps to the Display / Data / Filters sections.
    innerRatio: z.number().min(0.3).max(0.8).default(0.62),
    padAngle: z.number().min(0).max(0.06).default(0.02),
    cornerRadius: z.number().min(0).max(8).default(2),
    legendPosition: z.enum(["bottom", "right"]).default("bottom"),
  });

export type TypeMixConfig = z.infer<typeof typeMixConfigSchema>;

const pieValue = (d: DimensionSlice) => d.total;

/** A hovered slice, with the svg-relative pointer position for the tooltip. */
type HoveredSlice = { index: number; label: string; total: number; x: number; y: number };

function TypeMixRender({ w, h, config }: WidgetRenderContext<TypeMixConfig>) {
  const { data, slices, sliceColors, loading, error } = useShapedSlices({
    groupBy: config.groupBy,
    sort: config.sort,
    show: config.show,
    filters: config.filters,
  });
  const [hover, setHover] = useState<HoveredSlice | null>(null);
  // Click-through: hosted on a dashboard (drilldown context) inside a placed
  // card (instance context), a slice press opens the Data drawer on its rows.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="block" />;
  }
  if (slices.length === 0) {
    return <WidgetEmpty icon="shapes" title="No projects in range" />;
  }

  const total = slices.reduce((sum, d) => sum + d.total, 0);
  const isDonut = config.donut;
  const legendRight = config.legendPosition === "right";
  const showLegend = config.legend && (legendRight ? w >= 4 : h >= 3);

  const renderCentroid = (xy: [number, number], arc: PieArcDatum<DimensionSlice>) => {
    if (arc.endAngle - arc.startAngle < 0.25) return null;
    const text = config.percentages
      ? `${Math.round((arc.data.total / total) * 100)}%`
      : String(arc.data.total);
    return (
      <ChartText
        x={xy[0]}
        y={xy[1]}
        textAnchor="middle"
        verticalAnchor="middle"
        fontSize={10}
        fontWeight={600}
        on={sliceColors.token(arc.data.label)}
      >
        {text}
      </ChartText>
    );
  };

  // Press → publish the slice as data (dimension + covered category ids); the
  // Dashboard host opens this instance's Data drawer filtered to it. "Other"
  // covers every folded tail id.
  const handleArcClick =
    drill && instance
      ? (arc: PieArcDatum<DimensionSlice>) => {
          setHover(null);
          const s = arc.data;
          drill.openData(instance.instanceId, {
            kind: "dimension",
            dimension: config.groupBy,
            ids: sliceDrilldownIds(s, data, slices),
            label: s.label,
          });
        }
      : undefined;

  const chart = (
    <Box fill="flex" position="relative">
      <ChartFrame ariaLabel="Project mix">
        {({ innerWidth, innerHeight }) => {
          const cx = innerWidth / 2;
          const cy = innerHeight / 2;
          const outerRadius = Math.max(0, Math.min(cx, cy));
          const innerRadius = isDonut ? outerRadius * config.innerRatio : 0;
          return (
            <>
              <Pie
                data={slices}
                pieValue={pieValue}
                colorAccessor={(d) => sliceColors.scale(d.label)}
                pieSort={null}
                pieSortValues={null}
                top={cy}
                left={cx}
                outerRadius={outerRadius}
                innerRadius={innerRadius}
                padAngle={config.padAngle}
                cornerRadius={config.cornerRadius}
                centroid={renderCentroid}
                activeIndex={hover?.index ?? null}
                onArcHover={(arc, point) =>
                  setHover(
                    arc && point
                      ? {
                          index: arc.index,
                          label: arc.data.label,
                          total: arc.data.total,
                          x: point.x,
                          y: point.y,
                        }
                      : null,
                  )
                }
                onArcClick={handleArcClick}
              />
              {isDonut ? (
                <ChartText
                  x={cx}
                  y={cy}
                  textAnchor="middle"
                  verticalAnchor="middle"
                  fontSize={outerRadius >= 44 ? 24 : 16}
                  fontWeight={700}
                >
                  {total}
                </ChartText>
              ) : null}
            </>
          );
        }}
      </ChartFrame>
      {hover ? (
        <ChartTooltipCard left={hover.x} top={hover.y}>
          <WidgetChartTooltip
            label={hover.label}
            details={`${hover.total} ${hover.total === 1 ? "project" : "projects"} · ${Math.round((hover.total / total) * 100)}%`}
          />
        </ChartTooltipCard>
      ) : null}
    </Box>
  );

  const legend = showLegend ? (
    <LegendOrdinal
      scale={sliceColors.scale}
      direction={legendRight ? "column" : "row"}
      itemMargin={legendRight ? "0 0 4px 0" : "0 12px 0 0"}
      shapeMargin="0 6px 0 0"
    />
  ) : null;

  return legendRight ? (
    <Box direction="row" fill="flex" gap="sm" align="center">
      {chart}
      {legend}
    </Box>
  ) : (
    <Box direction="column" fill="flex" gap="sm">
      {chart}
      {legend}
    </Box>
  );
}

/** The Display / Data / Filters editor — v3-style icon rows throughout. */
const typeMixSettings: WidgetSettingsGroup[] = [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: [
      {
        key: "groupBy",
        kind: "select",
        label: "Group by",
        icon: "layers",
        options: WIDGET_DIMENSION_OPTIONS,
      },
      { key: "show", kind: "select", label: "Show", icon: "eye", options: SLICE_SHOW_OPTIONS },
      {
        key: "sort",
        kind: "select",
        label: "Sort by",
        icon: "arrow-down-wide-narrow",
        options: SLICE_SORT_OPTIONS,
      },
    ],
  },
  {
    title: "Display",
    fields: [
      { key: "donut", kind: "toggle", label: "Display as a donut", icon: "donut" },
      { key: "percentages", kind: "toggle", label: "Display as percentages", icon: "percent" },
      { key: "legend", kind: "toggle", label: "Show legend", icon: "list" },
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const typeMixDef: WidgetModule<TypeMixConfig> = {
  type: "type-mix",
  title: "Project mix",
  dynamicTitle: (config) => workloadTitle(config.title, config.groupBy),
  icon: "shapes",
  span: { minW: 3, minH: 3, maxW: 10, maxH: 10, defaultW: 4, defaultH: 4 },
  configSchema: typeMixConfigSchema,
  settings: typeMixSettings,
  headerActions: WidgetHeaderFilters,
  // Chart body: grab the card from anywhere on it — wedge/bar/point clicks
  // still land because drag activation needs 4px of movement.
  bodyDraggable: true,
  catalog: [
    {
      key: "type-mix.pie",
      label: "Pie chart",
      description: "Show a share of projects by type, stage, team, assignee, or health.",
      section: "Custom",
      defaultConfig: { donut: false, percentages: true, legend: true },
      defaultW: 4,
      defaultH: 4,
    },
    {
      key: "type-mix.donut",
      label: "Donut chart",
      description: "Show a share of projects with a center total.",
      section: "Custom",
      defaultConfig: { donut: true, percentages: true, legend: true },
      defaultW: 4,
      defaultH: 4,
    },
    {
      key: "type-mix.stage",
      label: "Workload by stage",
      description: "The share of projects in each pipeline stage, as a pie or donut.",
      section: "Charts",
      tags: ["projects", "stages"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "stage", donut: true, percentages: true, legend: true },
      defaultW: 4,
      defaultH: 4,
      // The label matches the derived title, so stay untitled and let
      // `dynamicTitle` track `groupBy` as the user regroups.
      autoTitle: false,
    },
    {
      key: "type-mix.assignee",
      label: "Workload by assignee",
      description: "The share of projects held by each assignee, as a pie or donut.",
      section: "Charts",
      tags: ["projects", "members"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "assignee", donut: true, percentages: true, legend: true },
      defaultW: 4,
      defaultH: 4,
      autoTitle: false,
    },
    {
      key: "type-mix.pipeline",
      label: "Pipeline distribution",
      description: "See the share of projects sitting in each stage.",
      section: "Charts",
      tags: ["projects", "stages"] satisfies readonly WidgetTag[],
      defaultConfig: { groupBy: "stage", donut: true, percentages: true, legend: true },
      defaultW: 4,
      defaultH: 4,
    },
  ],
  render: TypeMixRender,
};
